// ================================================================
// API Client
// ================================================================

const API_BASE =
  import.meta.env.VITE_API_URL ||
  (typeof window !== 'undefined' && window.location.hostname === 'localhost'
    ? '' // use vite proxy
    : 'https://api-llc.ilutech.mx');

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE}${path}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  };

  // Don't set Content-Type for FormData
  if (options.body instanceof FormData) {
    delete headers['Content-Type'];
  }

  const res = await fetch(url, {
    ...options,
    headers,
    credentials: 'include',
  });

  if (!res.ok) {
    const text = await res.text();
    let errData: unknown;
    try {
      errData = JSON.parse(text);
    } catch {
      errData = text;
    }
    throw new Error(
      typeof errData === 'object' && errData && 'error' in errData
        ? String((errData as { error: string }).error)
        : `HTTP ${res.status}`
    );
  }

  return res.json() as Promise<T>;
}

export const api = {
  health: () => request<{ status: string }>('/api/health'),

  entities: {
    list: () => request<{ entities: any[] }>('/api/entities'),
    get: (id: string) => request<{ entity: any }>(`/api/entities/${id}`),
    create: (data: any) =>
      request<{ id: string; entity: any }>('/api/entities', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    update: (id: string, data: any) =>
      request<{ entity: any }>(`/api/entities/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    delete: (id: string) =>
      request<{ deleted: boolean }>(`/api/entities/${id}`, {
        method: 'DELETE',
      }),
  },

  obligations: {
    list: (params?: { entity_id?: string; status?: string; jurisdiction?: string }) => {
      const qs = params
        ? '?' +
          new URLSearchParams(
            Object.entries(params).filter(([, v]) => v) as [string, string][]
          ).toString()
        : '';
      return request<{ obligations: any[] }>(`/api/obligations${qs}`);
    },
    get: (id: string) => request<{ obligation: any }>(`/api/obligations/${id}`),
    complete: (id: string, notes?: string) =>
      request<{ completed: boolean }>(`/api/obligations/${id}/complete`, {
        method: 'POST',
        body: JSON.stringify({ completion_notes: notes }),
      }),
    summary: () => request<any>('/api/obligations/timeline/summary'),
  },

  transactions: {
    list: (params?: { entity_id?: string; tax_year?: string }) => {
      const qs = params
        ? '?' +
          new URLSearchParams(
            Object.entries(params).filter(([, v]) => v) as [string, string][]
          ).toString()
        : '';
      return request<{ transactions: any[] }>(`/api/transactions${qs}`);
    },
    create: (data: any) =>
      request<{ id: string; remittance_tax_evaluation: any }>('/api/transactions', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    summary: (entityId: string, taxYear: number) =>
      request<any>(`/api/transactions/summary/${entityId}/${taxYear}`),
    delete: (id: string) =>
      request<{ deleted: boolean }>(`/api/transactions/${id}`, {
        method: 'DELETE',
      }),
  },

  documents: {
    list: (params?: { entity_id?: string; obligation_id?: string; kind?: string }) => {
      const qs = params
        ? '?' +
          new URLSearchParams(
            Object.entries(params).filter(([, v]) => v) as [string, string][]
          ).toString()
        : '';
      return request<{ documents: any[] }>(`/api/documents${qs}`);
    },
    upload: (formData: FormData) =>
      request<{ id: string; r2_key: string }>('/api/documents', {
        method: 'POST',
        body: formData,
      }),
    downloadUrl: (id: string) => `${API_BASE}/api/documents/${id}/download`,
    delete: (id: string) =>
      request<{ deleted: boolean }>(`/api/documents/${id}`, {
        method: 'DELETE',
      }),
  },

  notices: {
    list: (params?: { entity_id?: string; status?: string }) => {
      const qs = params
        ? '?' +
          new URLSearchParams(
            Object.entries(params).filter(([, v]) => v) as [string, string][]
          ).toString()
        : '';
      return request<{ notices: any[] }>(`/api/notices${qs}`);
    },
    create: (data: any) =>
      request<{ id: string }>('/api/notices', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    update: (id: string, data: any) =>
      request<{ updated: boolean }>(`/api/notices/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    delete: (id: string) =>
      request<{ deleted: boolean }>(`/api/notices/${id}`, {
        method: 'DELETE',
      }),
  },

  contracts: {
    list: (params?: { entity_id?: string }) => {
      const qs = params?.entity_id ? `?entity_id=${params.entity_id}` : '';
      return request<{ contracts: any[] }>(`/api/contracts${qs}`);
    },
    create: (data: any) =>
      request<{ id: string }>('/api/contracts', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
  },

  learning: {
    list: (category?: string) => {
      const qs = category ? `?category=${category}` : '';
      return request<{ topics: any[] }>(`/api/learning${qs}`);
    },
    get: (topicKey: string) =>
      request<{ topic: any }>(`/api/learning/${topicKey}`),
  },
};
